import { addDays, isAfter } from 'date-fns';
import { graphql } from '../__generated__';
import {
  ActiveSupplyPointFragment as ActiveSupplyPoint,
  MeterTechnicalDetailsFragment as MeterTechnicalDetails,
  RegistersFragment as Register,
} from '../__generated__/graphql';

export const RegistersFragment = graphql(/* kapi-codegen-graphql */ `
  fragment Registers on SupplyPointRegisterDetails {
    registerStartDate
    registerEndDate
  }
`);

export const MeterTechnicalDetailsFragment =
  graphql(/* kapi-codegen-graphql */ `
    fragment MeterTechnicalDetails on SupplyPointMeterTechnicalDetails {
      status
      registers {
        ...Registers
      }
    }
  `);

export const SupplyPointDetailsFragment = graphql(/* kapi-codegen-graphql */ `
  fragment SupplyPointDetails on SupplyPoint {
    status {
      current {
        status
        details {
          supplyStartDate
          supplyEndDate
        }
      }
    }
    meterTechnicalDetails {
      ...MeterTechnicalDetails
    }
  }
`);

export const ActiveSupplyPointFragment = graphql(/* kapi-codegen-graphql */ `
  fragment ActiveSupplyPoint on AccountSupplyPoint {
    startDate
    end {
      date
    }
    supplyPoint {
      ...SupplyPointDetails
    }
  }
`);

const METER_LIVE_STATUSES = ['Active'];
const ACCOUNT_SUPPLY_POINT_LIVE_STATUSES = [
  'Live',
  'Registering',
  'PendingLoss',
  'DisconnectionRequested',
];

const isNoDateOrDateInFuture = (date: string | null | undefined) =>
  !date || (!!date && isAfter(new Date(date), new Date()));

const isDateTodayOrPast = (date: string | null | undefined) =>
  !isNoDateOrDateInFuture(date);

const getStartOfNextDay = (dateString: string) => {
  return addDays(new Date(dateString).setHours(0, 0, 0, 0), 1).toDateString();
};

const isSupplyStartDateActive = (
  accountSupplyPoint: ActiveSupplyPoint,
): boolean => {
  const { startDate, supplyPoint } = accountSupplyPoint;

  // The account supply start date is associated with the customer's account
  const isAccountStartDateActive = isDateTodayOrPast(startDate);
  // The supply state is taken from the supply status snapshot and is not associated with any customer account
  const isSupplyStartDateActive = isDateTodayOrPast(
    supplyPoint.status?.current?.details?.supplyStartDate,
  );

  return isAccountStartDateActive && isSupplyStartDateActive;
};

const isSupplyEndDateActive = (
  accountSupplyPoint: ActiveSupplyPoint,
): boolean => {
  const { end, supplyPoint } = accountSupplyPoint;

  // The account supply start date is associated with the customer's account
  let accountSupplyPointsEndDate = end?.date;

  // The supply state is taken from the supply status snapshot and is not associated with any customer account
  let supplyPointEndDate = supplyPoint.status?.current?.details?.supplyEndDate;

  // set account supply end date to beginning of next day so that users can access their supply for the entirety of their last day
  if (typeof end?.date === 'string') {
    accountSupplyPointsEndDate = getStartOfNextDay(end.date);
  }

  // set supply end date to beginning of next day so that users can access their supply for the entirety of their last day
  if (typeof supplyPointEndDate === 'string') {
    supplyPointEndDate = getStartOfNextDay(supplyPointEndDate);
  }

  return (
    isNoDateOrDateInFuture(accountSupplyPointsEndDate) &&
    isNoDateOrDateInFuture(supplyPointEndDate)
  );
};

const isAccountSupplyPointActive = (accountSupplyPoint: ActiveSupplyPoint) => {
  const { supplyPoint } = accountSupplyPoint;

  const statusIsLive = supplyPoint.status?.current?.status
    ? ACCOUNT_SUPPLY_POINT_LIVE_STATUSES.includes(
        supplyPoint.status.current.status,
      )
    : false;

  return (
    isSupplyStartDateActive(accountSupplyPoint) &&
    statusIsLive &&
    isSupplyEndDateActive(accountSupplyPoint)
  );
};

const isMeterLive = (meterTechnicalDetails: MeterTechnicalDetails) =>
  meterTechnicalDetails.status
    ? METER_LIVE_STATUSES.includes(meterTechnicalDetails.status)
    : false;

const isRegisterActive = (register: Register) => {
  const isStartDateNotNullAndPast = !isNoDateOrDateInFuture(
    register?.registerStartDate,
  );

  return (
    isStartDateNotNullAndPast &&
    isNoDateOrDateInFuture(register.registerEndDate)
  );
};

export const isActiveSupplyPointMeterRegister = (
  accountSupplyPoint: ActiveSupplyPoint,
) => {
  if (!isAccountSupplyPointActive(accountSupplyPoint)) return false;

  const isSomeMeterActive =
    accountSupplyPoint.supplyPoint.meterTechnicalDetails.some(meter => {
      const statusIsLive = isMeterLive(meter);

      if (!statusIsLive) return false;

      const isSomeRegisterActive = meter.registers.some(register => {
        return isRegisterActive(register);
      });

      return isSomeRegisterActive;
    });

  return isSomeMeterActive;
};
